import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Show,
  Text,
  Textarea,
  ButtonGroup,
  useDisclosure,
  useToast,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Divider,
  SimpleGrid,
  Container,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";


import {
  FiUser,
  FiPhone,
  FiMapPin,
  FiEdit3,
  FiPlus,
  FiActivity,
  FiHome,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from "react-icons/fi";
import { MdVerified, MdLocationCity } from "react-icons/md";
import { BsTicketPerforated } from "react-icons/bs";
import Link from '../../../../components/Link';
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import formatDate from "../../../../helpers/formatDate";
import { translateKind } from "../../../../shared/libs/translate";
import { useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import { TicketState } from "../../../../components/States";
import formatDateTable from "../../../../helpers/FormatDateTable";
import getFullName from "../../../../helpers/FullName";
import CitySelect from "../../../../components/select/CitySelect";

import ShortenedText from "../../../../components/ShortenedText";
import Notes from "../../../../components/notes/Notes";
import { ModernTable } from "../../../../components/tables";
import MapBoxField from "../../../../components/map/MapBoxField";

// Phone number formatting function
const formatPhoneNumber = (value) => {
  if (!value) return '';

  // Sadece rakamları al
  const numbers = value.replace(/\D/g, '');

  // 10 haneli sınır
  const limited = numbers.slice(0, 10);

  // Formatla: 5XX XXX XX XX
  if (limited.length >= 7) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6, 8)} ${limited.slice(8, 10)}`;
  } else if (limited.length >= 4) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
  } else if (limited.length >= 1) {
    return `${limited.slice(0, 3)} ${limited.slice(3)}`;
  }
  return limited;
};

const cleanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const digits = String(phoneNumber).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length > 10 && digits.startsWith("90")) return digits.slice(-10);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
};
const formatPhoneNumberForAPI = (number) => "+90" + number;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// Modern Info Card Component
function ModernInfoCard({ title, value, icon, color = "blue", isVerified, onClick }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
        borderColor: `${color}.300`,
      }}
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
      animation={`${fadeIn} 0.6s ease-out`}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
            {isVerified !== undefined && (
              <Badge
                colorScheme={isVerified ? "green" : "red"}
                variant="subtle"
                borderRadius="full"
                px={2}
                py={1}
                fontSize="xs"
              >
                <Icon
                  as={isVerified ? FiCheckCircle : FiXCircle}
                  boxSize={3}
                  mr={1}
                />
                {isVerified ? "Doğrulandı" : "Doğrulanmadı"}
              </Badge>
            )}
          </HStack>
          <VStack align="stretch" spacing={1}>
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              {title}
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color={`${color}.600`}>
              {value}
            </Text>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
}

function CustomerForms({ customer }) {
  const [attachments, setAttachments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      if (!customer?.url) return;
      setLoading(true);
      try {
        const custId = urlToId(customer.url);
        const tResp = await client.get("/tickets/", { params: { customer: custId, page_size: 10 }, disableCache: true });
        const tickets = Array.isArray(tResp?.data?.results) ? tResp.data.results : (Array.isArray(tResp?.data) ? tResp.data : []);
        const all = [];
        for (const t of tickets) {
          const id = urlToId(t?.url) || t?.id;
          if (!id) continue;
          const aResp = await client.get("/attachments/", { params: { object_model: "ticket.ticket", object_id: id }, disableCache: true });
          const list = Array.isArray(aResp?.data?.results) ? aResp.data.results : (Array.isArray(aResp?.data) ? aResp.data : []);
          for (const att of list) all.push({ ...att, ticket_id: id });
        }
        setAttachments(all);
      } catch (_) {
        setAttachments([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customer]);

  if (loading) return <Spinner size="sm" />;
  if (!attachments.length) return <Text fontSize="sm" color="gray.500">Bu müşteriye ait form bulunmuyor.</Text>;

  return (
    <VStack align="stretch" spacing={3}>
      {attachments.map((att) => {
        const name = att?.file_name || "servis_formu.pdf";
        let href = att?.file_url || att?.file || "";
        const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
        if (href && !/^https?:\/\//.test(href)) {
          href = base + (href.startsWith("/") ? href : `/${href}`);
        }
        return (
          <HStack key={att?.url || name} justify="space-between">
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="500">{att?.description || name}</Text>
              <Text fontSize="xs" color="gray.500">İş Emri #{att.ticket_id}</Text>
            </VStack>
            {href ? (
              <Button as="a" href={href} download target="_blank" size="sm" variant="outline">
                PDF İndir
              </Button>
            ) : (
              <Text fontSize="xs" color="gray.400">Bağlantı yok</Text>
            )}
          </HStack>
        );
      })}
    </VStack>
  );
}

// Customer Stats Component
function CustomerStats({ ticketCount, unitCount, verificationStatus }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="md">Müşteri İstatistikleri</Heading>
          <Icon as={FiActivity} color="blue.500" />
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <SimpleGrid columns={3} spacing={4}>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Toplam Ticket</StatLabel>
            <StatNumber fontSize="2xl" color="blue.500">{ticketCount}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Ünite Sayısı</StatLabel>
            <StatNumber fontSize="2xl" color="green.500">{unitCount}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel fontSize="xs">Doğrulama</StatLabel>
            <StatNumber fontSize="lg" color={verificationStatus ? "green.500" : "red.500"}>
              {verificationStatus ? "✓" : "✗"}
            </StatNumber>
          </Stat>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
}

export async function customerdetailLoader({ params }) {
  const customer = (await client.get(`/customers/${params.customerID}/`)).data;
  const tickets = (await client.get(
    `/tickets/?page_size=100000&customer=${params.customerID}`
  )).data;
  const units = (await client.get('/units/', {
    params: {
      customer: params.customerID
    }
  })).data
  const products = (await client.get('/products/', {
    params: { customer: params.customerID, page_size: 100000 }
  })).data;
  const invoices = (await client.get('/ticket-invoices/', {
    params: { customer: params.customerID, page_size: 100000 }
  })).data;
  const payments = (await client.get('/invoice-payments/', {
    params: { customer: params.customerID, page_size: 100000 }
  })).data;
  return [customer, tickets, units, products, invoices, payments];
}

export default function CustomerDetail() {
  const revalidator = useRevalidator();
  const [customer, customerTickets, customerUnits, customerProducts, customerInvoices, customerPayments] = useLoaderData();
  const [firstName, setFirstName] = useState(customer.user.first_name || "");
  const [lastName, setLastName] = useState(customer.user.last_name || "");
  const [phoneNumber, setPhoneNumber] = useState(customer.phone_number || "");
  const [city, setCity] = useState(customer.city.url || "");
  const [address, setAddress] = useState(customer.address);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!customer.city || customer.city === '') return;
    setCity(customer.city)
  }, [customer?.city])

  const toast = useToast();
  const {
    isOpen: isOpenPhone,
    onOpen: onOpenPhone,
    onClose: onClosePhone,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const {
    isOpen: isOpenAddProduct,
    onOpen: onOpenAddProduct,
    onClose: onCloseAddProduct,
  } = useDisclosure();
  const {
    isOpen: isOpenAddPayment,
    onOpen: onOpenAddPayment,
    onClose: onCloseAddPayment,
  } = useDisclosure();
  const navigate = useNavigate();

  const bgGradient = "linear(135deg, blue.50 0%, purple.50 25%, pink.50 50%, orange.50 75%, yellow.50 100%)";

  const handleFieldChange = (field, value) => {
    if (field === 'phoneNumber') {
      const formatted = formatPhoneNumber(value);
      setPhoneNumber(formatted);
      validateField('phoneNumber', formatted);
    }
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    if (field === 'phoneNumber') {
      const cleanValue = value.replace(/\s/g, '');
      if (!cleanValue) {
        newErrors.phoneNumber = 'Telefon numarası gereklidir';
      } else if (!/^5\d{9}$/.test(cleanValue)) {
        newErrors.phoneNumber = 'Geçerli bir telefon numarası giriniz (5XX XXX XX XX)';
      } else {
        delete newErrors.phoneNumber;
      }
    }

    setErrors(newErrors);
  };

  const handleFieldBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleCustomer = () => {
    const cleanedPhoneNumber = cleanPhoneNumber(phoneNumber);
    if (!cleanedPhoneNumber || cleanedPhoneNumber.length !== 10 || !cleanedPhoneNumber.startsWith('5')) {
      toast({ title: "Hata", description: "Geçerli telefon numarası giriniz (5XX XXX XX XX)", status: "error", duration: 3000, isClosable: true });
      return;
    }
    const requestBody = {
      first_name: firstName,
      last_name: lastName,
      phone_number: formatPhoneNumberForAPI(cleanedPhoneNumber),
      city: city,
      address: address,
    };
    client
      .patch(`/customers/${urlToId(customer.url)}/`, requestBody)
      .then(() => {
        onCloseEdit();
        revalidator.revalidate();
        toast({
          title: "Başarılı",
          description: "Müşteri bilgileri güncellendi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          title: "Hata",
          description: "Müşteri bilgileri güncellenirken bir hata oluştu",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const onCreateTicket = () => {
    const normalized = cleanPhoneNumber(phoneNumber || customer?.phone_number || "");
    navigate({
      pathname: `/is-emri/olustur/`,
      search: `?phone_number=${encodeURIComponent(normalized)}`,
    });
  }

  const sendPhoneNumberVerification = () => {
    const requestBody = {
      phone_number: customer.phone_number,
      customer: customer.url,
    };
    client
      .post(`/customers/send_verification_sms/`, requestBody)
      .then(() => {
        onClosePhone();
        revalidator.revalidate();
        toast({
          title: "Onay kodu gönderildi",
          variant: "left-accent",
          description: `${customer.user.first_name} ${customer.user.last_name} adlı müşteriye ait ${customer.phone_number} telefon numarasını onaylamayı sağlayan link gönderildi`,
          status: "info",
          duration: 10000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          title: "Hata",
          description: "Onay kodu gönderilirken bir hata oluştu",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const ticketColumns = [
    {
      title: "İŞ TİPİ",
      dataIndex: "kind",
      render: (text) => (
        <Badge colorScheme="blue" variant="subtle" borderRadius="full">
          {translateKind(text)}
        </Badge>
      ),
    },
    {
      title: "Başlık",
      dataIndex: "body",
      render: (text, record) => (
        <VStack align="start" spacing={1}>
          <Link to={`/is-emri/${urlToId(record.url)}`} fontWeight="semibold">
            <ShortenedText text={text} maxLength={60} />
          </Link>
          <HStack spacing={2} fontSize="xs" color="gray.500">
            <Text>#{record.number}</Text>
            <Text>
              <Icon as={FiClock} mr={1} />
              {formatDate(record.created_at)}
            </Text>
            <Text>
              <Icon as={FiUser} mr={1} />
              {record.full_name}
            </Text>
          </HStack>
        </VStack>
      ),
    },
    {
      title: "ÜNİTE BİLGİSİ",
      dataIndex: "related_unit",
      render: (text) => (
        <Text fontSize="sm">{text ? text?.name : "--"}</Text>
      ),
    },
    {
      title: "Bayi",
      dataIndex: "assigned_to",
      render: (text) => (
        <Text fontSize="sm">{text ? text?.name : "--"}</Text>
      ),
    },
    {
      title: "Oluşturulma Tarihi",
      dataIndex: "created_at",
      render: (text) => (
        <Text fontSize="sm">{formatDateTable(text)}</Text>
      ),
    },
    {
      title: "Durum",
      dataIndex: "state",
      render: (state) => <TicketState state={state} />,
    },
  ];

  // Derived data for new tabs
  const unitKinds = Array.from(new Set((customerUnits?.results || customerUnits || []).map(u => u.project?.kind))).filter(Boolean);
  const addressHistoryMap = new Map();
  (customerTickets?.results || []).forEach(t => {
    if (t.address) {
      addressHistoryMap.set(t.address, (addressHistoryMap.get(t.address) || 0) + 1);
    }
  });
  const addressHistory = Array.from(addressHistoryMap.entries()).map(([address, count]) => ({ address, count }));
  const addressColumns = [
    { title: 'Adres', dataIndex: 'address', render: (text) => <ShortenedText text={text} maxLength={80} /> },
    { title: 'Geçiş Sayısı', dataIndex: 'count', render: (n) => <Badge colorScheme="blue">{n}</Badge> },
  ];
  const historyRows = (customerTickets?.results || []).map(t => ({
    created_at: t.created_at,
    unit_name: t.related_unit?.name || '--',
    address: t.address,
  }));
  const historyColumns = [
    { title: 'Tarih', dataIndex: 'created_at', render: (text) => <Text fontSize="sm">{formatDateTable(text)}</Text> },
    { title: 'Ünite', dataIndex: 'unit_name', render: (text) => <Text fontSize="sm">{text}</Text> },
    { title: 'Adres', dataIndex: 'address', render: (text) => <ShortenedText text={text} maxLength={80} /> },
  ];
  const productRows = (customerProducts?.results || []).map(p => ({
    code: p.code,
    exp_date: p.exp_date,
    is_expired: p.is_expired,
  }));
  const productColumns = [
    { title: 'Ürün Kodu', dataIndex: 'code', render: (text) => <Text fontSize="sm">{text}</Text> },
    { title: 'Son Kullanma', dataIndex: 'exp_date', render: (text) => <Text fontSize="sm">{text ? formatDateTable(text) : '-'}</Text> },
    { title: 'Durum', dataIndex: 'is_expired', render: (val) => <Badge colorScheme={val ? 'red' : 'green'}>{val ? 'Süresi Dolmuş' : 'Aktif'}</Badge> },
  ];
  const invoicesRows = (customerInvoices?.results || []).map(inv => ({
    invoice_number: inv.invoice_number,
    created_at: inv.created_at,
    total_amount: inv.total_amount,
    payment_made: inv.payment_made,
    ticket_number: inv.ticket_number,
  }));
  const invoicesColumns = [
    { title: 'Fatura No', dataIndex: 'invoice_number', render: (text) => <Text fontSize="sm">{text}</Text> },
    { title: 'Tarih', dataIndex: 'created_at', render: (text) => <Text fontSize="sm">{formatDateTable(text)}</Text> },
    { title: 'Tutar', dataIndex: 'total_amount', render: (val) => <Badge colorScheme="purple">{Number(val || 0).toFixed(2)}₺</Badge> },
    { title: 'Ödeme', dataIndex: 'payment_made', render: (val) => <Badge colorScheme={val ? 'green' : 'orange'}>{val ? 'Ödendi' : 'Beklemede'}</Badge> },
    { title: 'İş Emri', dataIndex: 'ticket_number', render: (num) => <Text fontSize="sm">#{num}</Text> },
  ];
  const totalAmount = invoicesRows.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0);
  const totalPayments = (customerPayments?.results || customerPayments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const unpaidAmount = totalAmount - totalPayments;

  // Add Product modal state
  const [selectedUnitUrl, setSelectedUnitUrl] = useState((customerUnits?.results?.[0]?.url) || (customerUnits?.[0]?.url) || "");
  const [productCode, setProductCode] = useState("");
  const [productModelId, setProductModelId] = useState("");
  const [expDate, setExpDate] = useState("");
  const [productModels, setProductModels] = useState([]);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  useEffect(() => {
    if (isOpenAddProduct) {
      client.get('/models/').then(res => setProductModels(res.data.results || res.data || []));
    }
  }, [isOpenAddProduct]);

  const handleAddProduct = () => {
    const unitId = urlToId(selectedUnitUrl);
    if (!unitId || !productCode) {
      toast({ title: 'Eksik bilgi', description: 'Ünite ve ürün kodu zorunlu', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    client.post(`/units/${unitId}/add-product/`, {
      product_code: productCode,
      model_id: productModelId || undefined,
      exp_date: expDate || undefined,
    }).then(() => {
      onCloseAddProduct();
      setProductCode("");
      setProductModelId("");
      setExpDate("");
      revalidator.revalidate();
      toast({ title: 'Ürün eklendi', status: 'success', duration: 3000, isClosable: true });
    }).catch(() => {
      toast({ title: 'Hata', description: 'Ürün eklenemedi', status: 'error', duration: 3000, isClosable: true });
    });
  };

  const handleAddPayment = () => {
    const invId = paymentInvoiceId || (customerInvoices?.results?.[0]?.id);
    if (!invId || !paymentAmount) {
      toast({ title: 'Eksik bilgi', description: 'Fatura ve tutar zorunlu', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    client.post('/invoice-payments/', {
      invoice: invId,
      amount: Number(paymentAmount),
      method: paymentMethod || undefined,
      note: paymentNote || undefined,
    }).then(() => {
      onCloseAddPayment();
      setPaymentInvoiceId('');
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentNote('');
      revalidator.revalidate();
      toast({ title: 'Ödeme eklendi', status: 'success', duration: 3000, isClosable: true });
    }).catch(() => {
      toast({ title: 'Hata', description: 'Ödeme eklenemedi', status: 'error', duration: 3000, isClosable: true });
    });
  };

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        opacity={0.1}
        backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 0%, transparent 50%)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header Section */}
          <Card
            bg={"rgba(255, 255, 255, 0.9)"}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 0.6s ease-out`}
          >
            <CardBody p={8}>
              <Flex direction={{ base: "column", lg: "row" }} gap={8} align="center">
                {/* Avatar Section */}
                <VStack spacing={4}>
                  <Avatar
                    size="2xl"
                    name={getFullName(customer.user)}
                    bg="blue.500"
                    color="white"
                    fontSize="2xl"
                    animation={`${pulse} 3s ease-in-out infinite`}
                  />
                  <VStack spacing={1} textAlign="center">
                    <Heading size="lg" color="blue.600">
                      {getFullName(customer.user)}
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      Müşteri ID: #{urlToId(customer.url)}
                    </Text>
                  </VStack>
                </VStack>

                {/* Info Cards Grid */}
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} flex={1}>
                  <ModernInfoCard
                    title="Telefon"
                    value={customer.phone_number}
                    icon={FiPhone}
                    color="green"
                    isVerified={customer.phone_verified}
                    onClick={!customer.phone_verified ? onOpenPhone : undefined}
                  />
                  <ModernInfoCard
                    title="Şehir"
                    value={customer.city_name}
                    icon={MdLocationCity}
                    color="purple"
                  />
                  <ModernInfoCard
                    title="Adres"
                    value={customer.address}
                    icon={FiMapPin}
                    color="orange"
                  />
                  {customerUnits.length > 0 && (
                    <ModernInfoCard
                      title="Ünite"
                      value={`${customerUnits[0].project.name}: ${customerUnits[0].apt_num}`}
                      icon={FiHome}
                      color="teal"
                    />
                  )}
                </SimpleGrid>

                {/* Action Buttons */}
                <VStack spacing={3}>
                  <Button
                    leftIcon={<Icon as={FiEdit3} />}
                    colorScheme="blue"
                    variant="solid"
                    size="md"
                    onClick={onOpenEdit}
                    borderRadius="xl"
                  >
                    Düzenle
                  </Button>
                  <Button
                    leftIcon={<Icon as={FiPlus} />}
                    colorScheme="green"
                    variant="outline"
                    size="md"
                    onClick={onCreateTicket}
                    borderRadius="xl"
                  >
                    İş Emri Oluştur
                  </Button>
                  {!customer.phone_verified && (
                    <Button
                      leftIcon={<Icon as={MdVerified} />}
                      colorScheme="orange"
                      variant="ghost"
                      size="sm"
                      onClick={onOpenPhone}
                      borderRadius="xl"
                    >
                      Telefon Doğrula
                    </Button>
                  )}
                </VStack>
              </Flex>
            </CardBody>
          </Card>

          {/* Stats Section */}
          <CustomerStats
            ticketCount={customerTickets.results.length}
            unitCount={customerUnits.length}
            verificationStatus={customer.phone_verified}
          />

          {/* Content Tabs */}
          <Card
            bg={"rgba(255, 255, 255, 0.9)"}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="2xl"
            overflow="hidden"
          >
            <CardBody p={0}>
              <Tabs variant="enclosed" colorScheme="blue">
                <TabList px={6} pt={6}>
                  <Tab>
                    <Icon as={BsTicketPerforated} mr={2} />
                    İş Emirleri ({customerTickets.results.length})
                  </Tab>
                  <Tab>
                    <Icon as={FiActivity} mr={2} />
                    Notlar
                  </Tab>
                  <Tab>
                    <Icon as={FiHome} mr={2} />
                    Geçmiş
                  </Tab>
                  <Tab>
                    <Icon as={FiActivity} mr={2} />
                    Ürünler ({productRows.length})
                  </Tab>
                  <Tab>
                    <Icon as={FiCheckCircle} mr={2} />
                    Cari Durum
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel p={6}>
                    <ModernTable
                      data={customerTickets.results}
                      columns={ticketColumns}
                      variant="minimal"
                      size="md"
                      showSearch={true}
                      showPagination={true}
                      pageSize={10}
                    />
                  </TabPanel>
                  <TabPanel p={6}>
                    <Notes object_model="customer.customer" object_id={urlToId(customer.url)} />
                  </TabPanel>
                  <TabPanel p={6}>
                    <VStack align="stretch" spacing={4}>
                      {unitKinds.length ? (
                        <HStack spacing={2} wrap="wrap">
                          {unitKinds.map(kind => (
                            <Badge key={kind} colorScheme="blue" variant="subtle">{translateKind(kind)}</Badge>
                          ))}
                        </HStack>
                      ) : null}
                      <ModernTable
                        data={historyRows}
                        columns={historyColumns}
                        variant="minimal"
                        size="md"
                        showSearch={false}
                        showPagination={true}
                        pageSize={10}
                      />
                      <ModernTable
                        data={addressHistory}
                        columns={addressColumns}
                        variant="minimal"
                        size="md"
                        showSearch={false}
                        showPagination={true}
                        pageSize={10}
                      />
                      <Box>
                        <Heading size="sm" mb={3}>Oluşturulmuş Formlar</Heading>
                        <CustomerForms customer={customer} />
                      </Box>
                    </VStack>
                  </TabPanel>
                  <TabPanel p={6}>
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between">
                        <Heading size="sm">Ürünler</Heading>
                        <Button leftIcon={<Icon as={FiPlus} />} colorScheme="blue" variant="solid" onClick={onOpenAddProduct}>Ürün Ekle</Button>
                      </HStack>
                      <ModernTable
                        data={productRows}
                        columns={productColumns}
                        variant="minimal"
                        size="md"
                        showSearch={true}
                        showPagination={true}
                        pageSize={10}
                      />
                    </VStack>
                  </TabPanel>
                  <TabPanel p={6}>
                    <VStack align="stretch" spacing={4}>
                      <SimpleGrid columns={3} spacing={4}>
                        <Stat textAlign="center">
                          <StatLabel fontSize="xs">Toplam Fatura</StatLabel>
                          <StatNumber fontSize="2xl" color="blue.500">{invoicesRows.length}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel fontSize="xs">Toplam Tutar</StatLabel>
                          <StatNumber fontSize="2xl" color="purple.500">{Number(totalAmount).toFixed(2)}₺</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel fontSize="xs">Toplam Ödenen</StatLabel>
                          <StatNumber fontSize="2xl" color="green.500">{Number(totalPayments).toFixed(2)}₺</StatNumber>
                        </Stat>
                      </SimpleGrid>
                      <SimpleGrid columns={3} spacing={4}>
                        <Stat textAlign="center">
                          <StatLabel fontSize="xs">Bekleyen Tutar</StatLabel>
                          <StatNumber fontSize="2xl" color="orange.500">{Number(unpaidAmount).toFixed(2)}₺</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel fontSize="xs">Bakiye</StatLabel>
                          <StatNumber fontSize="2xl" color={unpaidAmount > 0 ? 'orange.500' : 'green.500'}>{Number(unpaidAmount).toFixed(2)}₺</StatNumber>
                        </Stat>
                      </SimpleGrid>
                      <HStack justify="flex-end">
                        <Button leftIcon={<Icon as={FiPlus} />} colorScheme="green" onClick={onOpenAddPayment}>Ödeme Ekle</Button>
                      </HStack>
                      <ModernTable
                        data={invoicesRows}
                        columns={invoicesColumns}
                        variant="minimal"
                        size="md"
                        showSearch={false}
                        showPagination={true}
                        pageSize={10}
                      />
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </Container>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={isOpenEdit}
        onClose={onCloseEdit}
        motionPreset="slideInBottom"
        size="lg"
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg={"white"}
          borderRadius="2xl"
          border="1px solid"
          borderColor={"gray.200"}
        >
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FiEdit3} color="blue.500" />
              <Text>Müşteri Bilgilerini Düzenle</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Ad</FormLabel>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={customer.user.firstName}
                    borderRadius="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Soyad</FormLabel>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    borderRadius="lg"
                  />
                </FormControl>
              </SimpleGrid>
              <FormControl isInvalid={touched.phoneNumber && errors.phoneNumber}>
                <FormLabel fontSize="sm" fontWeight="medium">Telefon</FormLabel>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                  onBlur={() => handleFieldBlur('phoneNumber')}
                  placeholder="5XX XXX XX XX"
                  maxLength={13}
                  borderRadius="lg"
                  borderColor={touched.phoneNumber && errors.phoneNumber ? "red.300" : "gray.300"}
                />
                {touched.phoneNumber && errors.phoneNumber && (
                  <FormHelperText color="red.500">{errors.phoneNumber}</FormHelperText>
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Adres Harita</FormLabel>
                <Box>
                  <MapBoxField value={address} onChangeAddress={(addr) => setAddress(addr)} />
                </Box>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Şehir</FormLabel>
                <CitySelect
                  bgColor={"white"}
                  city={city}
                  setCity={setCity}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Adres</FormLabel>
                <Textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  borderRadius="lg"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme="blue"
                onClick={handleCustomer}
                borderRadius="lg"
              >
                Kaydet
              </Button>
              <Button
                variant="ghost"
                onClick={onCloseEdit}
                borderRadius="lg"
              >
                Vazgeç
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Product Modal */}
      <Modal isOpen={isOpenAddProduct} onClose={onCloseAddProduct} size="lg" closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200">
          <ModalHeader>
            <HStack spacing={3}><Icon as={FiPlus} color="blue.500" /><Text>Ürün Ekle</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Ünite</FormLabel>
                <select value={selectedUnitUrl} onChange={(e) => setSelectedUnitUrl(e.target.value)} style={{ padding: '8px', borderRadius: '8px', border: '1px solid #CBD5E0' }}>
                  {(customerUnits?.results || customerUnits || []).map(u => (
                    <option key={u.url || u.id} value={u.url}>{u.project?.name}: {u.apt_num}</option>
                  ))}
                </select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Ürün Modeli</FormLabel>
                <select value={productModelId} onChange={(e) => setProductModelId(e.target.value)} style={{ padding: '8px', borderRadius: '8px', border: '1px solid #CBD5E0' }}>
                  <option value="">Seçiniz</option>
                  {productModels.map(m => (
                    <option key={m.url || m.id} value={urlToId(m.url || `/models/${m.id}/`)}>{m.name} ({m.code})</option>
                  ))}
                </select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Ürün Kodu</FormLabel>
                <Input value={productCode} onChange={(e) => setProductCode(e.target.value)} placeholder="Seri/Ürün Kodu" borderRadius="lg" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Son Kullanma Tarihi</FormLabel>
                <Input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} borderRadius="lg" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="blue" onClick={handleAddProduct} borderRadius="lg">Ekle</Button>
              <Button variant="ghost" onClick={onCloseAddProduct} borderRadius="lg">Vazgeç</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Payment Modal */}
      <Modal isOpen={isOpenAddPayment} onClose={onCloseAddPayment} size="lg" closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200">
          <ModalHeader>
            <HStack spacing={3}><Icon as={FiPlus} color="green.500" /><Text>Ödeme Ekle</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Fatura</FormLabel>
                <select value={paymentInvoiceId} onChange={(e) => setPaymentInvoiceId(e.target.value)} style={{ padding: '8px', borderRadius: '8px', border: '1px solid #CBD5E0' }}>
                  <option value="">Seçiniz</option>
                  {(customerInvoices?.results || customerInvoices || []).map(inv => (
                    <option key={inv.id} value={inv.id}>{inv.invoice_number} - #{inv.ticket_number}</option>
                  ))}
                </select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Tutar</FormLabel>
                <Input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="0.00" borderRadius="lg" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Yöntem</FormLabel>
                <Input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} placeholder="Nakit/Havale/Kart" borderRadius="lg" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Not</FormLabel>
                <Textarea value={paymentNote} onChange={(e) => setPaymentNote(e.target.value)} rows={3} borderRadius="lg" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button colorScheme="green" onClick={handleAddPayment} borderRadius="lg">Ekle</Button>
              <Button variant="ghost" onClick={onCloseAddPayment} borderRadius="lg">Vazgeç</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Phone Verification Modal */}
      <Modal
        isOpen={isOpenPhone}
        onClose={onClosePhone}
        motionPreset="slideInBottom"
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg={"white"}
          borderRadius="2xl"
          border="1px solid"
          borderColor={"gray.200"}
        >
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={MdVerified} color="green.500" />
              <Text>Telefon Numarasını Doğrula</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={touched.phoneNumber && errors.phoneNumber}>
              <FormLabel fontSize="sm" fontWeight="medium">Telefon</FormLabel>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                onBlur={() => handleFieldBlur('phoneNumber')}
                placeholder="5XX XXX XX XX"
                maxLength={13}
                borderRadius="lg"
                borderColor={touched.phoneNumber && errors.phoneNumber ? "red.300" : "gray.300"}
              />
              {touched.phoneNumber && errors.phoneNumber && (
                <FormHelperText color="red.500">{errors.phoneNumber}</FormHelperText>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme="green"
                onClick={sendPhoneNumberVerification}
                isDisabled={!phoneNumber.trim()}
                borderRadius="lg"
              >
                Onay Kodu Gönder
              </Button>
              <Button
                variant="ghost"
                onClick={onClosePhone}
                borderRadius="lg"
              >
                Vazgeç
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
